import * as React from "react";
import { Navigate, useLocation, type RouteObject } from "react-router-dom";

import { RouteErrorBoundary, RouteSkeleton } from "@/components/ErrorBoundary";
import { InternalLayout } from "@/components/layout/InternalLayout";
import { InternalRouteGuard } from "@/components/layout/InternalRouteGuard";
import AdminAnalyticsPageComponent from "@/pages/admin/analytics/page";

const ContractSavingsCampaignsPage = React.lazy(() => import("@/pages/marketing/campaigns/page"));
const RoastingPage = React.lazy(() => import("@/pages/roasting/page"));
const RootPage = React.lazy(() => import("@/pages/root/page"));
const AdminPage = React.lazy(() => import("@/pages/admin/page"));
const AdminSecurityPage = React.lazy(
	() => import("@/pages/admin/security/page"),
);
const BolPage = React.lazy(() => import("@/pages/bol/page"));
const CargoLabelPage = React.lazy(() => import("@/pages/cargo-label/page"));
const InventoryCargosPage = React.lazy(
	() => import("@/pages/inventory/cargos/page"),
);
const InventoryProductsPage = React.lazy(
	() => import("@/pages/inventory/products/page"),
);
const InvoiceDetailPage = React.lazy(() => import("@/pages/invoice/_id/page"));
const CustomerInvoicePage = React.lazy(
	() => import("@/pages/invoice/_id/customer/page"),
);
const LoginPage = React.lazy(() => import("@/pages/login/page"));
const LogisticsOrdersPage = React.lazy(
	() => import("@/pages/logistics/orders/page"),
);
const LogisticsInboundDeliveriesPage = React.lazy(
	() => import("@/pages/logistics/inbound-deliveries/page"),
);
const LogisticsInternationalPurchasesPage = React.lazy(
	() => import("@/pages/logistics/international-purchases/page"),
);
const LogisticsOutboundShipmentsPage = React.lazy(
	() => import("@/pages/logistics/outbound-shipments/page"),
);
const OrderResumePage = React.lazy(() => import("@/pages/order-resume/page"));
const SalesPage = React.lazy(() => import("@/pages/sales/page"));
const SalesAnalyticsPage = React.lazy(
	() => import("@/pages/sales/analytics/page"),
);
const SalesOrdersPage = React.lazy(() => import("@/pages/sales/orders/page"));
const SalesContractDetailPage = React.lazy(
	() => import("@/pages/sales/contracts/_id/page"),
);
const SalesContractsPage = React.lazy(
	() => import("@/pages/sales/contracts/page"),
);
const SalesContractsNewPage = React.lazy(
	() => import("@/pages/sales/contracts/new/page"),
);
const SalesSamplesPage = React.lazy(() => import("@/pages/sales/samples/page"));
const VerifyTwoFactorPage = React.lazy(() => import("@/pages/verify-2fa/page"));
const WishlistPage = React.lazy(() => import("@/pages/wishlist/page"));
const SupportPage = React.lazy(() => import("@/pages/support/page"));
const SupportAgentsPage = React.lazy(
	() => import("@/pages/support/agents/page"),
);
const InventoryDashboard = React.lazy(() => import("@/pages/inventory/page"));
const InventorySamples = React.lazy(
	() => import("@/pages/inventory/samples/page"),
);
const InventoryReservations = React.lazy(
	() => import("@/pages/inventory/reservations/page"),
);
const InventoryAuditPage = React.lazy(
	() => import("@/pages/inventory/audit/page"),
);
const LogisticsDashboard = React.lazy(() => import("@/pages/logistics/page"));
const LogisticsShippingAdjustments = React.lazy(
	() => import("@/pages/logistics/shipping-adjustments/page"),
);
const LogisticsShippingQuotes = React.lazy(
	() => import("@/pages/logistics/shipping-quotes/page"),
);
const LogisticsCarrierNotices = React.lazy(
	() => import("@/pages/logistics/carrier-notices/page"),
);
const LogisticsUsers = React.lazy(() => import("@/pages/logistics/users/page"));
const AccountingPage = React.lazy(
	() => import("@/pages/admin/accounting/page"),
);
const AccountingOrdersPage = React.lazy(
	() => import("@/pages/admin/accounting/orders/page"),
);
const AccountingBillsPage = React.lazy(
	() => import("@/pages/admin/accounting/bills/page"),
);
const AccountingBillDetailPage = React.lazy(
	() => import("@/pages/admin/accounting/bills/_id/page"),
);
const AccountingBillsSettingsPage = React.lazy(
	() => import("@/pages/admin/accounting/bills/settings/page"),
);
const AccountingBillsAciPage = React.lazy(
	() => import("@/pages/admin/accounting/bills/aci/page"),
);
const AdminCopilotPage = React.lazy(() => import("@/pages/admin/copilot/page"));
const AdminDiscountsPage = React.lazy(
	() => import("@/pages/admin/discounts/page"),
);
const MarketingEventsPage = React.lazy(() => import("@/pages/admin/events/page"));
const AdminKnowledgebasePage = React.lazy(
	() => import("@/pages/admin/knowledgebase/page"),
);
const AdminFlowPage = React.lazy(() => import("@/pages/admin/flow/page"));
const AdminCronsPage = React.lazy(() => import("@/pages/admin/crons/page"));
const AdminLeadsPage = React.lazy(() => import("@/pages/admin/leads/page"));
const SalesLeadsPage = React.lazy(() => import("@/pages/sales/leads/page"));
const SalesFollowUpsPage = React.lazy(
	() => import("@/pages/sales/follow-ups/page"),
);
const SalesCrmPage = React.lazy(() => import("@/pages/sales/crm/page"));
const SalesEmailsPage = React.lazy(() => import("@/pages/sales/emails/page"));
const SalesEmailsCreatePage = SalesEmailsPage;
const MarketingEmailsPage = SalesEmailsPage;
const MarketingEmailsCreatePage = SalesEmailsPage;
const SalesUsersPage = React.lazy(() => import("@/pages/sales/users/page"));
const DocsViewerPage = React.lazy(() => import("@/pages/docs/page"));
const ChangelogPage = React.lazy(() => import("@/pages/changelog/page"));

function withRouteBoundary(
	element: React.ReactNode,
	loadingElement: React.ReactNode = <RouteSkeleton />,
) {
	return (
		<RouteErrorBoundary>
			<React.Suspense fallback={loadingElement}>{element}</React.Suspense>
		</RouteErrorBoundary>
	);
}

function withInternalRouteBoundary(element: React.ReactNode) {
	return withRouteBoundary(element, null);
}

function RedirectWithSearch({ to }: { to: string }) {
	const location = useLocation();
	return <Navigate to={`${to}${location.search}`} replace />;
}

/** Standalone pages rendered outside the internal layout */
export const standaloneRoutes: RouteObject[] = [
	{ path: "/", element: withRouteBoundary(<RootPage />) },
	{ path: "/login", element: withRouteBoundary(<LoginPage />) },
	{ path: "/verify-2fa", element: withRouteBoundary(<VerifyTwoFactorPage />) },
	{ path: "/bol", element: withRouteBoundary(<BolPage />) },
	{ path: "/crm/samples", element: <RedirectWithSearch to="/sales/samples" /> },
	{ path: "/sales/crm/samples", element: <RedirectWithSearch to="/sales/samples" /> },
	{
		path: "/cargo-label",
		element: withRouteBoundary(
			<InternalRouteGuard requiredPath="/cargo-label">
				<CargoLabelPage />
			</InternalRouteGuard>,
		),
	},
	{
		path: "/invoice/:id/customer",
		element: withRouteBoundary(
			<InternalRouteGuard requiredPath="/invoice">
				<CustomerInvoicePage />
			</InternalRouteGuard>,
		),
	},
	{
		path: "/invoice/:id",
		element: withRouteBoundary(
			<InternalRouteGuard requiredPath="/invoice">
				<InvoiceDetailPage />
			</InternalRouteGuard>,
		),
	},
	{ path: "/order-resume", element: withRouteBoundary(<OrderResumePage />) },
	{ path: "/wishlist", element: withRouteBoundary(<WishlistPage />) },
];

/** Routes wrapped by the unified InternalLayout with sidebar */
export const internalLayoutRoute: RouteObject = {
	element: withInternalRouteBoundary(<InternalLayout />),
	children: [
		{ path: "/roasting/*", element: withInternalRouteBoundary(<RoastingPage />) },
		// Admin
		{ path: "/admin", element: withInternalRouteBoundary(<AdminPage />) },
		{
			path: "/admin/accounting/bills/settings",
			element: withInternalRouteBoundary(<AccountingBillsSettingsPage />),
		},
		{
			path: "/admin/accounting/bills/aci",
			element: withInternalRouteBoundary(<AccountingBillsAciPage />),
		},
		{
			path: "/admin/accounting/bills/:billId",
			element: withInternalRouteBoundary(<AccountingBillDetailPage />),
		},
		{
			path: "/admin/accounting/bills",
			element: withInternalRouteBoundary(<AccountingBillsPage />),
		},
		{
			path: "/admin/accounting/orders",
			element: withInternalRouteBoundary(<AccountingOrdersPage />),
		},
		{
			path: "/admin/accounting",
			element: withInternalRouteBoundary(<AccountingPage />),
		},
		{
			path: "/admin/accounting/:section",
			element: withInternalRouteBoundary(<AccountingPage />),
		},
		{
			path: "/admin/analytics",
			element: withInternalRouteBoundary(<AdminAnalyticsPageComponent />),
		},
		{
			path: "/admin/copilot",
			element: withInternalRouteBoundary(<AdminCopilotPage />),
		},
		{
			path: "/admin/discounts",
			element: withInternalRouteBoundary(<AdminDiscountsPage />),
		},
		{
			path: "/admin/events",
			element: <Navigate to="/marketing/events" replace />,
		},
		{
			path: "/marketing/campaigns",
			element: withInternalRouteBoundary(<ContractSavingsCampaignsPage />),
		},
		{
			path: "/marketing/events",
			element: withInternalRouteBoundary(<MarketingEventsPage />),
		},
		{
			path: "/admin/knowledgebase/*",
			element: withInternalRouteBoundary(<AdminKnowledgebasePage />),
		},
		{
			path: "/admin/flow",
			element: withInternalRouteBoundary(<AdminFlowPage />),
		},
		{
			path: "/security",
			element: withInternalRouteBoundary(<AdminSecurityPage />),
		},
		{
			path: "/admin/security",
			element: <Navigate to="/security" replace />,
		},
		{
			path: "/admin/crons",
			element: withInternalRouteBoundary(<AdminCronsPage />),
		},
		{
			path: "/admin/leads",
			element: withInternalRouteBoundary(<AdminLeadsPage />),
		},
		// Sales
		{
			path: "/sales/leads",
			element: <RedirectWithSearch to="/sales/crm/leads" />,
		},
		{
			path: "/sales/follow-ups",
			element: <RedirectWithSearch to="/sales/crm/follow-ups" />,
		},
		{
			path: "/sales/crm/opportunities",
			element: withInternalRouteBoundary(<SalesCrmPage />),
		},
		{
			path: "/sales/crm/leads",
			element: withInternalRouteBoundary(<SalesLeadsPage />),
		},
		{
			path: "/sales/crm/follow-ups",
			element: withInternalRouteBoundary(<SalesFollowUpsPage />),
		},
		{
			path: "/sales/crm/people",
			element: withInternalRouteBoundary(<SalesUsersPage />),
		},
		{
			path: "/sales/crm",
			element: <RedirectWithSearch to="/sales/crm/opportunities" />,
		},
		{
			path: "/marketing/users",
			element: withInternalRouteBoundary(<SalesUsersPage />),
		},
		{
			path: "/marketing/emails/create",
			element: withInternalRouteBoundary(<MarketingEmailsCreatePage />),
		},
		{
			path: "/marketing/emails",
			element: withInternalRouteBoundary(<MarketingEmailsPage />),
		},
		{
			path: "/sales/emails/create",
			element: <Navigate to="/marketing/emails/create" replace />,
		},
		{
			path: "/sales/emails",
			element: <Navigate to="/marketing/emails" replace />,
		},
		{
			path: "/sales/users",
			element: <RedirectWithSearch to="/sales/crm/people" />,
		},
		{
			path: "/sales/discount",
			element: <Navigate to="/admin/discounts" replace />,
		},
		{
			path: "/sales/discounts",
			element: <Navigate to="/admin/discounts" replace />,
		},
		// Support
		{ path: "/support", element: withInternalRouteBoundary(<SupportPage />) },
		{
			path: "/support/agents",
			element: withInternalRouteBoundary(<SupportAgentsPage />),
		},
		// Inventory
		{
			path: "/inventory",
			element: withInternalRouteBoundary(<InventoryDashboard />),
		},
		{
			path: "/inventory/cargos",
			element: withInternalRouteBoundary(<InventoryCargosPage />),
		},
		{
			path: "/inventory/products",
			element: withInternalRouteBoundary(<InventoryProductsPage />),
		},
		{
			path: "/inventory/samples",
			element: withInternalRouteBoundary(<InventorySamples />),
		},
		{
			path: "/inventory/reservations",
			element: withInternalRouteBoundary(<InventoryReservations />),
		},
		{
			path: "/inventory/audit",
			element: withInternalRouteBoundary(<InventoryAuditPage />),
		},
		// Logistics
		{
			path: "/logistics",
			element: withInternalRouteBoundary(<LogisticsDashboard />),
		},
		{
			path: "/logistics/orders",
			element: withInternalRouteBoundary(<LogisticsOrdersPage />),
		},
		{
			path: "/logistics/inbound-deliveries",
			element: withInternalRouteBoundary(<LogisticsInboundDeliveriesPage />),
		},
		{
			path: "/logistics/international-purchases",
			element: withInternalRouteBoundary(<LogisticsInternationalPurchasesPage />),
		},
		{
			path: "/logistics/outbound-shipments",
			element: withInternalRouteBoundary(<LogisticsOutboundShipmentsPage />),
		},
		{
			path: "/logistics/shipping-adjustments",
			element: withInternalRouteBoundary(<LogisticsShippingAdjustments />),
		},
		{
			path: "/logistics/shipping-quotes",
			element: withInternalRouteBoundary(<LogisticsShippingQuotes />),
		},
		{
			path: "/logistics/carrier-notices",
			element: withInternalRouteBoundary(<LogisticsCarrierNotices />),
		},
		{
			path: "/logistics/users",
			element: withInternalRouteBoundary(<LogisticsUsers />),
		},
		// Sales
		{ path: "/sales", element: withInternalRouteBoundary(<SalesPage />) },
		{
			path: "/sales/analytics",
			element: withInternalRouteBoundary(<SalesAnalyticsPage />),
		},
		{
			path: "/sales/orders",
			element: withInternalRouteBoundary(<SalesOrdersPage />),
		},
		{
			path: "/sales/contracts/:id",
			element: withInternalRouteBoundary(<SalesContractDetailPage />),
		},
		{
			path: "/sales/contracts",
			element: withInternalRouteBoundary(<SalesContractsPage />),
		},
		{
			path: "/sales/contracts/new",
			element: withInternalRouteBoundary(<SalesContractsNewPage />),
		},
		{
			path: "/sales/samples",
			element: withInternalRouteBoundary(<SalesSamplesPage />),
		},
		// Docs (read-only viewer, no sidebar entry)
		{ path: "/docs", element: withInternalRouteBoundary(<DocsViewerPage />) },
		{ path: "/changelog", element: withInternalRouteBoundary(<ChangelogPage />) },
		{
			path: "/docs/:slug",
			element: withInternalRouteBoundary(<DocsViewerPage />),
		},
	],
};
